import { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'

import Input from './Input'
import classes from './CodeInput.module.scss'

const CodeInput = ({ length, value, placeholder, onChange, ...props }) => {
  const [selectAll, setSelectAll] = useState(false)
  const inputsRef = useRef([])
  const placeholderDigits = (typeof placeholder === 'string' ? placeholder : '').padEnd(length, ' ').slice(0, length).split('')
  const digits = (typeof value === 'string' ? value : '').padEnd(length, ' ').slice(0, length).split('')

  const editableIndex = digits.findIndex((d) => d === ' ')
  const focusIdx = editableIndex === -1 ? length - 1 : editableIndex
  const focusInput = (idx) => inputsRef.current[idx]?.focus()

  useEffect(() => {
    focusInput(focusIdx)
  }, [focusIdx])

  const handleChange = (idx, e) => {
    if (idx !== focusIdx) {
      return
    }

    const val = e.target.value.replace(/\D/g, '').slice(-1)
    const next = [...digits]
    next[idx] = val || ' '
    onChange(next.join(''))

    if (val && idx < length - 1) {
      focusInput(idx + 1)
    }
  }

  const handleKeyDown = (idx, e) => {
    const { key, ctrlKey, metaKey } = e

    if ((ctrlKey || metaKey) && key.toLowerCase() === 'a') {
      e.preventDefault()
      setSelectAll(true)
      return
    }

    if (selectAll && (key === 'Backspace' || key === 'Delete')) {
      e.preventDefault()
      onChange(Array(length).fill(' ').join(''))
      setSelectAll(false)
      focusInput(0)
      return
    }

    if (selectAll && /\d/.test(key)) {
      e.preventDefault()
      const next = Array(length).fill('')
      next[0] = key
      onChange(next.join(''))
      setSelectAll(false)
      if (length > 1) {
        focusInput(1)
      }
      return
    }

    if (key === 'Backspace') {
      e.preventDefault()
      if (idx !== focusIdx) {
        return
      }

      const next = [...digits]

      if (next[idx]?.trim()) {
        next[idx] = ''
        onChange(next.join(''))
      } else if (idx > 0) {
        next[idx - 1] = ''
        onChange(next.join(''))
        focusInput(idx - 1)
      }
      return
    }

    if (selectAll) {
      setSelectAll(false)
    }
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const pasted = (e.clipboardData || window.clipboardData)
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, length)

    if (!pasted) {
      return
    }

    const next = pasted.split('')
    while (next.length < length) {
      next.push('')
    }
    onChange(next.join(''))
    focusInput(pasted.length >= length ? length - 1 : pasted.length)
  }

  const handleCopy = (e) => {
    e.preventDefault()
    navigator.clipboard.writeText(digits.join(''))
  }

  return (
    <div className='d-flex align-items-center'>
      {digits.map((digit, idx) => (
        <div key={idx} className={idx !== length - 1 ? 'mr-2' : ''}>
          <Input
            type='text'
            inputMode='numeric'
            pattern='\d*'
            maxLength='1'
            value={digit === ' ' ? '' : digit}
            placeholder={value.trim() ? '' : placeholderDigits[idx]}
            readOnly={idx !== focusIdx}
            innerRef={(el) => (inputsRef.current[idx] = el)}
            classNames={{ inputContainerWrapper: classes.codeInputWrapper, inputContainer: classes.codeInputContainer, input: classes.codeInput }}
            aria-label={`Digit ${idx + 1}`}
            onChange={(e) => handleChange(idx, e)}
            onKeyDown={(e) => handleKeyDown(idx, e)}
            onPaste={handlePaste}
            onCopy={handleCopy}
            {...props}
          />
        </div>
      ))}
    </div>
  )
}

CodeInput.propTypes = {
  length: PropTypes.number,
  value: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onCompleted: PropTypes.func,
  name: PropTypes.string,
}

CodeInput.defaultProps = {
  length: 4,
  value: '',
  placeholder: '',
  name: 'code',
}
export default CodeInput
